import { authApi } from '@web/app/features/auth'
import { appApi } from '@web/app/store/api'
import { profileApi } from '@web/app/features/profile/store/changePasswordApi'
import type { AuthType, JWTMetadataType } from '@web/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { jwtDecode } from 'jwt-decode'
import { accessActions } from '@web/app/features/access/store/accessSlice'

export interface AuthState {
  loading: boolean
  error: string | null
  authData: Partial<AuthType> & {
    entity_id?: string
    entity_type?: string
    entity_name?: string
  }
  isDemo: boolean
  profileEntitiesManual?: boolean
}

const initialState: AuthState = {
  loading: false,
  error: null,
  authData: {},
  profileEntitiesManual: false,
  isDemo: false,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    setIsDemo(state, action: PayloadAction<boolean>) {
      state.isDemo = action.payload
    },
    logout(state) {
      state.loading = false
      state.error = null
      state.authData = {}
      state.profileEntitiesManual = false // 💡 Resetear esto es vital para el nuevo usuario
    },
    setDummyState(state, { payload }: PayloadAction<Partial<AuthType>>) {
      state.authData = {
        ...state.authData,
        ...payload,
      }
    },
    setProfileEntities(
      state,
      action: PayloadAction<{ entity_id?: string; entity_type?: string; entity_name?: string }>
    ) {
      state.authData = {
        ...state.authData,
        ...action.payload,
      }
      state.profileEntitiesManual = true
    },
    setAccessToken(state, action: PayloadAction<string>) {
      state.authData.token = action.payload
    },
    setRefreshToken(state, action: PayloadAction<string>) {
      state.authData.refresh_token = action.payload
    },
  },
  selectors: {
    accessToken: (state) => state.authData.token,
    externalToken: (state) => state.authData.external_token,
    refreshToken: (state) => state.authData.refresh_token,
    isAuthenticated: (state) => !!state.authData.token,
    isLoading: (state) => state.loading,
    expiresIn: (state) => state.authData.expires_in,
    expires: (state) => state.authData.expires,
    userId: (state) => state.authData.user_id,
    role: (state) => state.authData.role,
    canFetchApi: (state) => !state.loading && !!state.authData.token,
    authData: (state) => state.authData,
    isDemo: (state) => state.isDemo,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        authApi.endpoints.token.matchPending(action) ||
        appApi.endpoints.refreshToken.matchPending(action),
      (state) => {
        state.loading = true
      }
    )
    builder.addMatcher(
      (action) =>
        authApi.endpoints.token.matchRejected(action) ||
        appApi.endpoints.refreshToken.matchRejected(action),
      (state) => {
        state.loading = false
      }
    )
    builder.addMatcher(authApi.endpoints.token.matchFulfilled, (state, { payload }) => {
      const decoded = jwtDecode<JWTMetadataType>(payload.token)
      state.authData = {
        ...payload,
        ...decoded,
        ...(state.profileEntitiesManual
          ? {
              entity_id: state.authData.entity_id,
              entity_type: state.authData.entity_type,
              entity_name: state.authData.entity_name,
            }
          : {}),
      }
      state.loading = false
      state.error = null
    })
    builder.addMatcher(appApi.endpoints.refreshToken.matchFulfilled, (state, { payload }) => {
      const decoded = jwtDecode<JWTMetadataType>(payload.token)
      state.authData = {
        ...state.authData,
        ...payload,
        ...decoded,
        ...(state.profileEntitiesManual
          ? {
              entity_id: state.authData.entity_id,
              entity_type: state.authData.entity_type,
              entity_name: state.authData.entity_name,
            }
          : {}),
      }
      state.loading = false
      state.error = null
    })
  },
})

export const authReducer = authSlice.reducer
export const authActions = authSlice.actions
export const authSelectors = authSlice.selectors

export const logoutUser = () => (dispatch: any) => {
  // 1. Limpiamos las rutas de acceso del Sidebar inmediatamente
  dispatch(accessActions.setRoutes([]))

  // 2. Borra token y datos de auth
  dispatch(authActions.logout())

  // 3. Resetea el cache de las APIs para que al entrar el siguiente no vea data vieja
  dispatch(appApi.util.resetApiState())
  dispatch(authApi.util.resetApiState())
}

export const fetchAndPersistProfileEntities = () => async (dispatch: any, getState: any) => {
  const state = getState()
  // Only fetch if not manually updated
  if (!state.auth.profileEntitiesManual) {
    const result = await dispatch(profileApi.endpoints.getProfile.initiate())
    if (result.data) {
      const { entity_id, entity_type, entity_name } = result.data
      dispatch(authActions.setProfileEntities({ entity_id, entity_type, entity_name }))
    }
  }
}
